<script setup>
import { reactive, computed, ref, watch, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { useRoute } from 'vue-router';
import { useWorkspaceV2 } from 'dashboard/composables/useWorkspaceV2';
import { useAlert, useTrack } from 'dashboard/composables';
import ContactAPI from 'dashboard/api/contacts';
import { debounce } from '@chatwoot/utils';
import { CONTACTS_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';

import Button from 'dashboard/components-next/button/Button.vue';
import ContactMergeForm from 'dashboard/components-next/Contacts/ContactsForm/ContactMergeForm.vue';

const props = defineProps({
  selectedContact: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['merged', 'resetTab']);

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const { isWorkspaceV2 } = useWorkspaceV2();

const state = reactive({
  primaryContactId: null,
});

const uiFlags = useMapGetter('contacts/getUIFlags');

const searchResults = ref([]);
const isSearching = ref(false);
const mergePending = ref(false);
const mergeError = ref('');
const searchError = ref('');
const searchQuery = ref('');
let searchRevision = 0;

const validationRules = {
  primaryContactId: { required },
};

const v$ = useVuelidate(validationRules, state);

const isMergingContact = computed(() => uiFlags.value.isMerging);

const primaryContactList = computed(
  () =>
    searchResults.value?.map(item => ({
      value: item.id,
      label: isWorkspaceV2.value
        ? [item.name, item.email || item.phone_number]
            .filter(Boolean)
            .join(' · ')
        : `(ID: ${item.id}) ${item.name}`,
    })) ?? []
);

const searchContacts = debounce(
  async ({ query, revision, accountId, contactId }) => {
    const isCurrent = () =>
      revision === searchRevision &&
      String(route.params.accountId) === accountId &&
      Number(props.selectedContact.id) === contactId;
    if (!isCurrent()) return;
    isSearching.value = true;
    try {
      const {
        data: { payload },
      } = await ContactAPI.search(query);
      if (!isCurrent()) return;
      searchResults.value = payload.filter(
        contact => contact.id !== props.selectedContact.id
      );
    } catch (error) {
      if (isCurrent()) {
        searchError.value = t(
          'CONTACTS_LAYOUT.SIDEBAR.MERGE.SEARCH_ERROR_MESSAGE'
        );
        if (!isWorkspaceV2.value) useAlert(searchError.value);
      }
    } finally {
      if (isCurrent()) isSearching.value = false;
    }
  },
  300,
  false
);

const invalidateContactSearch = ({ clearSelection = false } = {}) => {
  searchRevision += 1;
  searchQuery.value = '';
  searchResults.value = [];
  searchError.value = '';
  isSearching.value = false;
  if (clearSelection) state.primaryContactId = null;
};

const scheduleContactSearch = (query, { clearSelection = true } = {}) => {
  const normalizedQuery = String(query || '').trim();
  searchRevision += 1;
  const revision = searchRevision;
  searchQuery.value = normalizedQuery;
  searchResults.value = [];
  searchError.value = '';
  if (clearSelection) state.primaryContactId = null;
  if (!normalizedQuery) {
    isSearching.value = false;
    return;
  }
  isSearching.value = true;
  searchContacts({
    query: normalizedQuery,
    revision,
    accountId: String(route.params.accountId),
    contactId: Number(props.selectedContact.id),
  });
};

const onContactSearch = query => scheduleContactSearch(query);
const retryContactSearch = () =>
  scheduleContactSearch(searchQuery.value, { clearSelection: false });

watch(
  [() => props.selectedContact.id, () => route.params.accountId],
  () => invalidateContactSearch({ clearSelection: true }),
  { flush: 'sync' }
);
onBeforeUnmount(() => invalidateContactSearch());

const resetState = () => {
  if (mergePending.value || isMergingContact.value) return;
  mergeError.value = '';
  invalidateContactSearch();
  if (state.primaryContactId === null) {
    emit('resetTab');
  }
  state.primaryContactId = null;
  searchResults.value = [];
  isSearching.value = false;
};

const onMergeContacts = async () => {
  if (mergePending.value || isMergingContact.value) return;
  const childId = props.selectedContact.id || route.params.contactId;
  const accountId = route.params.accountId;
  const isCurrentContact = () =>
    accountId === route.params.accountId &&
    Number(childId) ===
      Number(props.selectedContact.id || route.params.contactId);
  mergePending.value = true;
  mergeError.value = '';
  try {
    const isFormValid = await v$.value.$validate();
    if (!isFormValid || !isCurrentContact()) return;
    await store.dispatch('contacts/merge', {
      childId,
      parentId: state.primaryContactId,
    });
    useTrack(CONTACTS_EVENTS.MERGED_CONTACTS);
    if (!isCurrentContact()) return;
    mergePending.value = false;
    // SiteUp: quem unificou quer conferir o contato que ficou, não a lista.
    emit('merged', state.primaryContactId);
    useAlert(t('CONTACTS_LAYOUT.SIDEBAR.MERGE.SUCCESS_MESSAGE'));
    resetState();
  } catch (error) {
    if (!isCurrentContact()) return;
    mergeError.value = t('CONTACTS_LAYOUT.SIDEBAR.MERGE.ERROR_MESSAGE');
    if (!isWorkspaceV2.value) useAlert(mergeError.value);
  } finally {
    mergePending.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-8 px-6">
    <div class="flex flex-col gap-2">
      <h4 class="text-base text-n-slate-12">
        {{ t('CONTACTS_LAYOUT.SIDEBAR.MERGE.TITLE') }}
      </h4>
      <p class="text-sm text-n-slate-11">
        {{ t('CONTACTS_LAYOUT.SIDEBAR.MERGE.DESCRIPTION') }}
      </p>
    </div>
    <fieldset
      class="m-0 min-w-0 border-0 p-0"
      :disabled="mergePending || isMergingContact"
    >
      <ContactMergeForm
        v-model:primary-contact-id="state.primaryContactId"
        :selected-contact="selectedContact"
        :primary-contact-list="primaryContactList"
        :is-searching="isSearching"
        :has-error="!!v$.primaryContactId.$error"
        :error-message="
          v$.primaryContactId.$error
            ? t('CONTACTS_LAYOUT.SIDEBAR.MERGE.PRIMARY_REQUIRED_ERROR')
            : ''
        "
        @search="onContactSearch"
      />
    </fieldset>
    <div v-if="searchError" class="flex items-center gap-3" role="alert">
      <p class="m-0 min-w-0 flex-1 text-sm text-n-ruby-11">
        {{ searchError }}
      </p>
      <Button
        v-if="isWorkspaceV2"
        variant="faded"
        color="slate"
        :label="t('CONTACTS_LAYOUT.SIDEBAR.MERGE.RETRY_SEARCH')"
        class="shrink-0"
        :disabled="isSearching || mergePending || isMergingContact"
        @click="retryContactSearch"
      />
    </div>
    <p v-if="mergeError" role="alert" class="m-0 text-sm text-n-ruby-11">
      {{ mergeError }}
    </p>
    <div
      class="flex gap-3"
      :class="
        isWorkspaceV2 ? 'flex-col sm:flex-row' : 'items-center justify-between'
      "
    >
      <Button
        variant="faded"
        color="slate"
        :label="t('CONTACTS_LAYOUT.SIDEBAR.MERGE.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        :class="{ '!min-h-11 !rounded-xl': isWorkspaceV2 }"
        :disabled="mergePending || isMergingContact"
        @click="resetState"
      />
      <Button
        :label="t('CONTACTS_LAYOUT.SIDEBAR.MERGE.BUTTONS.CONFIRM')"
        class="w-full"
        :class="{ '!min-h-11 !rounded-xl': isWorkspaceV2 }"
        :is-loading="mergePending || isMergingContact"
        :disabled="mergePending || isMergingContact"
        @click="onMergeContacts"
      />
    </div>
  </div>
</template>
